import { Card } from '@app/components/various/Card';
import { compiledPaths } from '@app/paths';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Row } from '@tanstack/react-table';
import { Account, Buyer } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BuyersList } from '../BuyersList';

interface Props {
  row: Row<Omit<Account, 'buyers'> & { buyers: Buyer.Base[] }>;
}

export const AccountListCard = ({ row }: Props) => {
  const { t } = useTranslation(['accounts', 'buyers', 'common']);
  const { trackEvent } = useAmplitude();
  const { account, name, email, buyers } = row.original;

  const handleExpand = () => {
    row?.getToggleExpandedHandler()();
    trackEvent({ name: 'accountsTable.button.expander.click' });
  };

  return (
    <Card.Wrapper>
      <Card.Header>
        {t('common:id')}: {account}
      </Card.Header>
      <Card.Title>
        <Link
          to={isDefined(account) ? compiledPaths.ACCOUNT_DETAILS({ accountId: account }) : ''}
          className="absolute-cursor-pointer"
          onClick={() => trackEvent({ name: 'accountsTable.link.row.click' })}
        >
          <span data-sentry-mask>{name}</span>
        </Link>
      </Card.Title>
      {isDefined(email) && !isEmpty(email) && (
        <div className="flex whitespace-pre">
          {email?.split(',').map((singleEmail, index) => (
            <React.Fragment key={index}>
              {index !== 0 && ', '}
              <Card.Link key={singleEmail} to={`mailto:${singleEmail}`} className="z-interactiveTableItem w-fit max-w-full truncate">
                {singleEmail}
              </Card.Link>
            </React.Fragment>
          ))}
        </div>
      )}
      <Card.AttributeList>
        <Card.Attribute
          name={t('buyers:buyer_other')}
          value={buyers.map(({ firstName, lastName }) => `${firstName} ${lastName}`.trim()).join(', ')}
        />
      </Card.AttributeList>

      <Card.Expander expanderLabel="Toggle details" isExpanded={row.getIsExpanded()} toggleExpanded={handleExpand}>
        <div className="relative z-interactiveTableItem mt-4 border-t border-neutral-96 pt-4 sm:pt-2">
          <BuyersList
            accountId={row.original.account}
            buyers={buyers}
            title={t('buyers:buyers_in_account', { account: row.original.name })}
          />
        </div>
      </Card.Expander>
    </Card.Wrapper>
  );
};
